import { Zap, Bot, PlayCircle, Palette, Rocket, User, Camera, Store, Video, Building2, Calculator, Shield, TestTube, Phone, Users, FileText, Sparkles, Gift, BookOpen, Mail, Calendar } from "lucide-react";
import { Navbar1 } from "@/components/ui/shadcnblocks-com-navbar1";
import { useLocation, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";

const Navigation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const isEnterprisePage = location.pathname === '/enterprise';
  const isSeoLandingPage = location.pathname === '/create-social-content-calendars-with-ai';
  const selfServePages = ['/startups', '/creators-and-influencers', '/retail', '/freelancers'];
  const isSelfServePage = selfServePages.includes(location.pathname);
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  useEffect(() => {
    const userData = localStorage.getItem("gimmefy_user_data");
    try {
      const parsed = JSON.parse(userData || "{}");
      if (parsed?.accessToken) {
        setIsLoggedIn(true);
      }
    } catch {
      console.error("Invalid gimmefy_user_data");
      setIsLoggedIn(false);
    }
  }, []);

  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const handleSignupClick = () => {
    if (isLoggedIn) {
      window.location.href = '/dashboard/home';
      return;
    }

    if (isSelfServePage) {
      window.location.href = '/signup/free';
    } else if (isSeoLandingPage) {
      window.location.href = '/signup/contentcal';
    } else if (isEnterprisePage) {
      const meetTheCrewSection = document.getElementById('meet-the-crew');
      if (meetTheCrewSection) {
        meetTheCrewSection.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      navigate('/enterprise#meet-the-crew');
    }
  };

  const handleLoginClick = () => {
    if (!isLoggedIn) {
      window.location.href = '/login';
    }
  };

  const handleEnterpriseNavigate = (anchor: string) => {
    if (location.pathname === '/enterprise') {
      const element = document.getElementById(anchor);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    } else {
      navigate(`/enterprise#${anchor}`);
    }
  };

  const handleLogoClick = () => {
    if (location.pathname === '/') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      navigate('/');
    }
  };

  const navigationData = {
    logo: {
      url: "/",
      src: "https://images.squarespace-cdn.com/content/6712145f4a2aba0c43ebb02d/c0fc8354-2e53-474d-a206-279098ebdfd1/Artboard+20.png?content-type=image%2Fpng",
      alt: "gimmefy",
      title: "gimmefy",
      onClick: handleLogoClick
    },
    menu: [
      {
        title: "Platform",
        url: "#",
        items: [
          {
            title: "Unmatched Capability",
            description: "See what makes gimmefy different",
            url: "/comparison",
            icon: <Sparkles className="h-4 w-4 text-primary" />
          },
          {
            title: "Studio",
            description: "AI-powered content creation workspace",
            url: "/studio",
            icon: <Zap className="h-4 w-4 text-primary" />
          },
          {
            title: "Playbooks",
            description: "Automated workflows and campaign templates",
            url: "/playbooks",
            icon: <PlayCircle className="h-4 w-4 text-primary" />
          },
          {
            title: "Visual Lab",
            description: "Advanced design and visual content tools",
            url: "/visual-lab",
            icon: <Palette className="h-4 w-4 text-primary" />
          },
          {
            title: "Agents",
            description: "Specialized AI assistants for every task",
            url: "/agents",
            icon: <Bot className="h-4 w-4 text-primary" />
          },
          {
            title: "Getting Started",
            description: "Video tutorials & quick start guide",
            url: "/getting-started",
            icon: <PlayCircle className="h-4 w-4 text-primary" />
          }
        ]
      },
      {
        title: "Enterprise",
        url: "#",
        items: [
          {
            title: "Overview",
            description: "Enterprise platform overview",
            url: "/enterprise#enterprise-overview",
            icon: <Building2 className="h-4 w-4 text-primary" />,
            onClick: () => handleEnterpriseNavigate('enterprise-overview')
          },
          {
            title: "AI Lab",
            description: "Custom AI automation and integrations",
            url: "/enterprise#ai-lab",
            icon: <Bot className="h-4 w-4 text-primary" />,
            onClick: () => handleEnterpriseNavigate('ai-lab')
          },
          {
            title: "Creation Desk",
            description: "Managed content creation services",
            url: "/enterprise#creation-desk",
            icon: <Users className="h-4 w-4 text-primary" />,
            onClick: () => handleEnterpriseNavigate('creation-desk')
          },
          {
            title: "Contact Sales",
            description: "Connect with our specialists",
            url: "/enterprise#meet-the-crew",
            icon: <Phone className="h-4 w-4 text-primary" />,
            onClick: () => handleEnterpriseNavigate('meet-the-crew')
          },
          {
            title: "Security",
            description: "Data protection and compliance",
            url: "/enterprise#security-trust-section",
            icon: <Shield className="h-4 w-4 text-primary" />,
            onClick: () => handleEnterpriseNavigate('security-trust-section')
          }
        ]
      },
      {
        title: "Self Serve",
        url: "#",
        items: [
          {
            title: "Startups",
            description: "Launch faster with AI-powered marketing",
            url: "/startups",
            icon: <Rocket className="h-4 w-4 text-primary" />
          },
          {
            title: "Creators and Influencers",
            description: "Create engaging content that converts",
            url: "/creators-and-influencers",
            icon: <Camera className="h-4 w-4 text-primary" />
          },
          {
            title: "Retail",
            description: "Drive sales with compelling product marketing",
            url: "/retail",
            icon: <Store className="h-4 w-4 text-primary" />
          },
          {
            title: "Freelancers",
            description: "Produce on-brand content for multiple brands",
            url: "/freelancers",
            icon: <User className="h-4 w-4 text-primary" />
          }
        ]
      },
      {
        title: "Resources",
        url: "#",
        items: [
          {
            title: "gimmefy Meet ups",
            description: "Community events",
            url: "/prompt-and-plate",
            icon: <Calendar className="h-4 w-4 text-primary" />
          },
          {
            title: "Gallery",
            description: "AI-powered videos and content",
            url: "/ai-ad-gallery",
            icon: <Video className="h-4 w-4 text-primary" />
          },
          {
            title: "Free 360 Brand Report",
            description: "Brand insights",
            url: "/get-free-brand-report",
            icon: <FileText className="h-4 w-4 text-primary" />
          },
          {
            title: "Free Demo",
            description: "Test drive features",
            url: "/enterprise#enterprise-form",
            icon: <TestTube className="h-4 w-4 text-primary" />,
            onClick: () => handleEnterpriseNavigate('enterprise-form')
          },
          {
            title: "ROI Calculator",
            description: "Calculate your savings",
            url: "/roi-calculator",
            icon: <Calculator className="h-4 w-4 text-primary" />
          },
          {
            title: "Tell a Friend",
            description: "Referral program",
            url: "/tellafriend",
            icon: <Gift className="h-4 w-4 text-primary" />
          },
          {
            title: "Case Studies",
            description: "Customer success stories",
            url: "/customer-stories",
            icon: <FileText className="h-4 w-4 text-primary" />
          },
          {
            title: "Blog",
            description: "Marketing insights and news",
            url: "https://blog.gimmefy.ai/",
            icon: <FileText className="h-4 w-4 text-primary" />,
            external: true
          },
          {
            title: "Daily Circus",
            description: "Daily marketing inspiration",
            url: "/daily-circus",
            icon: <Sparkles className="h-4 w-4 text-primary" />
          }
        ]
      },
      {
        title: "Support",
        url: "#",
        items: [
          {
            title: "Knowledge Base",
            description: "Find answers",
            url: "https://knowledgebase.gimmefy.ai/portal/en/kb",
            icon: <BookOpen className="h-4 w-4 text-primary" />,
            external: true
          },
          {
            title: "Getting Started",
            description: "Quick start guide",
            url: "/getting-started",
            icon: <PlayCircle className="h-4 w-4 text-primary" />
          },
          {
            title: "Contact Us",
            description: "Get in touch",
            url: "/contact",
            icon: <Mail className="h-4 w-4 text-primary" />
          }
        ]
      }
    ],
    mobileExtraLinks: [],
    auth: {
      login: {
        text: isLoggedIn ? "" : "Sign In",
        url: "/login",
        href: "/login",
        onClick: handleLoginClick
      },
      signup: {
        text: isLoggedIn ? "Go to Dashboard" : (isSelfServePage ? "Start free" : "Book Demo"),
        url: isLoggedIn ? "/dashboard/home" : (isSelfServePage ? "/signup/free" : "#"),
        href: isLoggedIn ? "/dashboard/home" : (isSelfServePage ? "/signup/free" : "#"),
        onClick: handleSignupClick
      }
    }
  };

  return <Navbar1 {...navigationData} />;
};

export default Navigation;
